import React, { useState, useEffect } from 'react';
import { BookOpen, Users, Calendar, Award, ArrowRight, Play, Star, Globe, Zap, Shield, ChevronDown } from 'lucide-react';

export default function PeacecraftLanding() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: BookOpen,
      title: "Interactive Library",
      description: "Access thousands of curated books and resources",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Users,
      title: "Community Learning",
      description: "Connect with learners and experts worldwide",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Calendar,
      title: "Live Events",
      description: "Join workshops, seminars, and interactive sessions",
      color: "from-orange-500 to-red-500"
    }
  ];

  const stats = [
    { number: "50K+", label: "Active Learners" },
    { number: "1000+", label: "Books Available" },
    { number: "500+", label: "Expert Instructors" },
    { number: "98%", label: "Success Rate" }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-40 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/95 backdrop-blur-md border-b border-slate-700' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Peacecraft
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#" className="text-slate-300 hover:text-white transition-colors">Features</a>
              <a href="#" className="text-slate-300 hover:text-white transition-colors">Library</a>
              <a href="#" className="text-slate-300 hover:text-white transition-colors">Events</a>
              <a href="#" className="text-slate-300 hover:text-white transition-colors">Community</a>
            </div>
            <button              onClick={() => (window.location = '/PeaceAuth')}
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700">
                  <Star className="w-4 h-4 text-yellow-500 mr-2" />
                  <span className="text-sm text-slate-300">Trusted by 50,000+ learners worldwide</span>
                </div>
                <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                  Learn.{' '}
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Grow.
                  </span>{' '}
                  Transform.
                </h1>
                <p className="text-xl text-slate-300 leading-relaxed max-w-2xl">
                  Join the ultimate learning platform where knowledge meets innovation. Access curated books, 
                  connect with experts, and participate in transformative events that shape your future.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button              onClick={() => (window.location = '/PeaceAuth')}
                className="group bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
                  Get Started Free
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="group border-2 border-slate-600 hover:border-slate-500 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:bg-slate-800 flex items-center justify-center">
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-white">{stat.number}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 bg-gradient-to-r from-slate-800 to-slate-700 rounded-3xl p-8 border border-slate-600">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl p-4 border border-blue-500/30">
                    <BookOpen className="w-8 h-8 text-blue-400 mb-2" />
                    <div className="text-sm font-semibold">Digital Library</div>
                    <div className="text-xs text-slate-400">1000+ Resources</div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl p-4 border border-purple-500/30">
                    <Users className="w-8 h-8 text-purple-400 mb-2" />
                    <div className="text-sm font-semibold">Community</div>
                    <div className="text-xs text-slate-400">50K+ Members</div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-2xl p-4 border border-orange-500/30">
                  <Calendar className="w-8 h-8 text-orange-400 mb-2" />
                  <div className="text-sm font-semibold">Live Events</div>
                  <div className="text-xs text-slate-400">Weekly workshops & seminars</div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl transform rotate-1"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Why Choose{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Peacecraft?
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience learning like never before with our cutting-edge platform designed for the modern learner.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`group relative p-8 rounded-3xl border border-slate-700 transition-all duration-500 hover:border-slate-600 cursor-pointer ${
                    activeFeature === index ? 'bg-slate-800/50 scale-105' : 'bg-slate-800/20'
                  }`}
                  onMouseEnter={() => setActiveFeature(index)}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500`}></div>
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-white transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-slate-300 leading-relaxed group-hover:text-slate-200 transition-colors">
                    {feature.description}
                  </p>
                  <div className="mt-6 flex items-center text-blue-400 group-hover:text-blue-300 transition-colors">
                    <span className="font-semibold">Learn More</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-3xl p-12 border border-slate-600 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10"></div>
            <div className="relative z-10">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Ready to Start Your{' '}
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Learning Journey?
                </span>
              </h2>
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Join thousands of learners who are already transforming their lives through knowledge and community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button              onClick={() => (window.location = '/PeaceAuth')}
                className="group bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
                  Get Started Now
                  <Zap className="w-5 h-5 ml-2 group-hover:animate-pulse" />
                </button>
                <button className="border-2 border-slate-600 hover:border-slate-500 px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:bg-slate-800 flex items-center justify-center">
                  <Globe className="w-5 h-5 mr-2" />
                  Explore Platform
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Peacecraft
              </span>
            </div>
            <div className="text-slate-400 text-sm">
              © 2025 Peacecraft. Empowering minds through knowledge.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}